import React, { useState, useRef, useEffect } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import html2pdf from 'html2pdf.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye, Edit } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';
import app from '../../firebase.js';

// Import images
import edaplogo from "../../img/edaplogo.png";
import crlogo from "../../img/crlogo.png";
import rectorSig from "../../UNCR/img/rector.png";
import perezSig from "../../img/perez.png";
import bapSig from "../../img/bap_sig.png";
import './UNILISUNDDiploma.css';

const UNILISUNDDiplomaTemplate = () => {
    const [formData, setFormData] = useState({
        noms: '',
        dateNaissance: '',
        lieuNaissance: '',
        province: '',
        pays: '',
        department: '',
        specialite: '',
        titreSuffix: '',
        englishTitle: 'PhD',
        gradeType: 'Doctorat',
        gradeTypeCustom: '',
        mention: 'Très Honorable',
        anneeAcademique: '',
        numeroDoc: '',
        dateEmission: new Date().toISOString().split('T')[0],
        language: 'Français + English'
    });
    const [documents, setDocuments] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [qrCodeData, setQrCodeData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const db = getDatabase(app);

    // Load documents from Firebase
    useEffect(() => {
        const documentsRef = ref(db, `unilis-und-diploma/EDAP/`);
        onValue(documentsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const documentArray = Object.keys(data).map((key) => ({
                    id: key,
                    ...data[key],
                }));
                setDocuments(documentArray);
            }
        });
    }, [db]);

    // Generate QR Code
    useEffect(() => {
        const generateQRCode = async () => {
            if (!selectedDocument) return;
            
            let qrData = `UNILIS UND DIPLOMA - EDAP\n`;
            Object.keys(selectedDocument).forEach(key => {
                if (key !== 'createdAt' && selectedDocument[key]) {
                    qrData += `${key}: ${selectedDocument[key]}\n`;
                }
            });
            qrData += `Status: Document Verified\nIssued by: Université de Lisala - UNILIS`;
            
            try {
                const qrCodeUrl = await QRCode.toDataURL(qrData, {
                    width: 120,
                    margin: 1,
                    color: { dark: '#000000', light: '#FFFFFF' }
                });
                setQrCodeData(qrCodeUrl);
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };
        if (selectedDocument) generateQRCode();
    }, [selectedDocument]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'gradeType' && value !== 'Autre') {
            setFormData({ ...formData, [name]: value, gradeTypeCustom: '' });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validate required fields
        const requiredFields = ['noms', 'dateNaissance', 'lieuNaissance', 'pays', 'department', 'specialite', 'titreSuffix', 'gradeType', 'mention', 'anneeAcademique', 'dateEmission'];
        for (const field of requiredFields) {
            if (!formData[field] || formData[field].trim() === '') {
                alert(`${field} est obligatoire!`);
                return;
            }
        }
        // Validate custom grade type if selected
        if (formData.gradeType === 'Autre' && (!formData.gradeTypeCustom || formData.gradeTypeCustom.trim() === '')) {
            alert('Veuillez spécifier le type de diplôme personnalisé');
            return;
        }

        // Save to Firebase
        const documentRefPath = isEditing 
            ? ref(db, `unilis-und-diploma/EDAP/${editingId}`)
            : ref(db, `unilis-und-diploma/EDAP/${formData.noms.replace(/\s+/g, '_')}_${Date.now()}`);
        
        const dataToSave = isEditing 
            ? { ...formData }
            : { ...formData, createdAt: new Date().toISOString() };
        
        set(documentRefPath, dataToSave)
            .then(() => {
                console.log(isEditing ? 'UNILIS UND diploma updated successfully' : 'UNILIS UND diploma saved successfully');
                message.success(isEditing ? 'Document modifié avec succès' : 'Document créé avec succès');
                resetForm();
            })
            .catch((error) => {
                console.error('Error saving UNILIS UND diploma:', error);
                message.error(isEditing ? 'Erreur lors de la modification' : 'Erreur lors de la création');
            });
    };

    const showDocumentModal = (document) => {
        setSelectedDocument(document);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedDocument(null);
    };

    const resetForm = () => {
        setFormData({
            noms: '',
            dateNaissance: '',
            lieuNaissance: '',
            province: '',
            pays: '',
            department: 'Sciences Juridiques',
            specialite: 'Droit des Affaires et Fiscalité',
            titreSuffix: 'DE DOCTORAT',
            englishTitle: 'PhD',
            gradeType: 'Doctorat',
            gradeTypeCustom: '',
            mention: 'Très Honorable',
            anneeAcademique: '',
            numeroDoc: '',
            dateEmission: new Date().toISOString().split('T')[0],
            language: 'Français + English'
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEditDocument = (document) => {
        setFormData({
            noms: document.noms || '',
            dateNaissance: document.dateNaissance || '',
            lieuNaissance: document.lieuNaissance || '',
            province: document.province || '',
            pays: document.pays || '',
            department: document.department || 'Sciences Juridiques',
            specialite: document.specialite || 'Droit des Affaires et Fiscalité',
            titreSuffix: document.titreSuffix || 'DE DOCTORAT',
            englishTitle: document.englishTitle || 'PhD',
            gradeType: document.gradeType || 'Doctorat',
            gradeTypeCustom: document.gradeTypeCustom || '',
            mention: document.mention || 'Très Honorable',
            anneeAcademique: document.anneeAcademique || '',
            numeroDoc: document.numeroDoc || '',
            dateEmission: document.dateEmission || new Date().toISOString().split('T')[0],
            language: document.language || 'Français + English'
        });
        setIsEditing(true);
        setEditingId(document.id);
        
        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteDocument = (id) => {
        remove(ref(db, `unilis-und-diploma/EDAP/${id}`))
            .then(() => {
                message.success('Document supprimé avec succès');
            })
            .catch((error) => {
                console.error('Error deleting document:', error);
                message.error('Erreur lors de la suppression');
            });
    };

    const formatDateWithMonthName = (dateStr) => {
        const date = new Date(dateStr);
        const months = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const downloadPDF = async () => {
        if (!selectedDocument) return;

        const element = document.getElementById('und-diploma-document');
        if (!element) {
            console.error('Document element not found');
            return;
        }

        try {
            const pdf = new jsPDF('l', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth(); // 842pt
            const pageHeight = pdf.internal.pageSize.getHeight(); // 595pt

            // Get all document pages
            const pages = element.querySelectorAll('.und-diploma-page');
            
            if (pages.length === 0) {
                message.error('Aucune page trouvée');
                return;
            }

            // Process each page individually (like bulletin.js)
            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                
                // Ensure white background for capture
                page.style.backgroundColor = 'white';

                // Wait for images to load
                const images = page.querySelectorAll('img');
                await Promise.all(Array.from(images).map(img => new Promise((resolve) => {
                    if (img.complete) resolve();
                    else { img.onload = resolve; img.onerror = resolve; }
                })));

                await new Promise(resolve => setTimeout(resolve, 400));

                const canvas = await html2canvas(page, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#FFFFFF',
                    windowWidth: page.scrollWidth,
                    windowHeight: page.scrollHeight
                });

                // Calculate scaling to fit page properly
                const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
                const w = canvas.width * ratio;
                const h = canvas.height * ratio;
                const x = (pageWidth - w) / 2; // Center horizontally
                const y = 20; // Small top margin

                if (i > 0) pdf.addPage();
                
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, w, h);
            }

            pdf.save(`${selectedDocument.noms?.replace(/\s+/g, '_') || 'document'}_UNILIS_UND_diploma.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Erreur lors de la génération du PDF');
        }
    };

    return (
        <div className="und-diploma-container">
            <div className="und-diploma-form-section">
                <div style={{ textAlign: 'center' }}>
                    <img src={edaplogo} alt="Logo UNILIS" className="und-diploma-form-logo" />
                    <h2>Formulaire de Diplôme UND - Université de Lisala</h2>
                </div>

                <form onSubmit={handleSubmit} className="und-diploma-form">
                    {/* Informations personnelles */}
                    <div className="und-diploma-form-section-title">Informations personnelles</div>
                    <div className="und-diploma-form-row">
                        <input
                            type="text"
                            name="noms"
                            value={formData.noms}
                            onChange={handleInputChange}
                            placeholder="Nom complet"
                            required
                            className="und-diploma-form-input"
                        />
                        <input
                            type="date"
                            name="dateNaissance"
                            value={formData.dateNaissance}
                            onChange={handleInputChange}
                            required
                            className="und-diploma-form-input"
                        />
                    </div>

                    <div className="und-diploma-form-row">
                        <input
                            type="text"
                            name="lieuNaissance"
                            value={formData.lieuNaissance}
                            onChange={handleInputChange}
                            placeholder="Lieu de naissance"
                            required
                            className="und-diploma-form-input"
                        />
                        <input
                            type="text"
                            name="province"
                            value={formData.province}
                            onChange={handleInputChange}
                            placeholder="Province (optionnel)"
                            className="und-diploma-form-input"
                        />
                    </div>

                    <div className="und-diploma-form-row">
                        <input
                            type="text"
                            name="pays"
                            value={formData.pays}
                            onChange={handleInputChange}
                            placeholder="Pays"
                            required
                            className="und-diploma-form-input"
                        />
                    </div>

                    {/* Informations académiques */}
                    <div className="und-diploma-form-section-title">Informations académiques</div>
                    <div className="und-diploma-form-row">
                        <input
                            type="text"
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            placeholder="Département (ex: Sciences Juridiques)"
                            required
                            className="und-diploma-form-input"
                        />
                        <input
                            type="text"
                            name="specialite"
                            value={formData.specialite}
                            onChange={handleInputChange}
                            placeholder="Spécialité"
                            required
                            className="und-diploma-form-input"
                        />
                    </div>

                    <div className="und-diploma-form-row" style={{ display: formData.gradeType === 'Autre' ? 'block' : 'grid' }}>
                        <select
                            name="gradeType"
                            value={formData.gradeType}
                            onChange={handleInputChange}
                            required
                            className="und-diploma-form-input"
                        >
                            <option value="Doctorat">Doctorat</option>
                            <option value="Master">Master</option>
                            <option value="HDR">HDR</option>
                            <option value="Licence">Licence</option>
                            <option value="Autre">Autre</option>
                        </select>
                        {formData.gradeType === 'Autre' && (
                            <input
                                type="text"
                                name="gradeTypeCustom"
                                value={formData.gradeTypeCustom}
                                onChange={handleInputChange}
                                placeholder="Spécifiez le type de diplôme personnalisé"
                                required={formData.gradeType === 'Autre'}
                                className="und-diploma-form-input"
                                style={{ marginTop: '10px' }}
                            />
                        )}
                    </div>

                    <div className="und-diploma-form-row">
                        <select
                            name="mention"
                            value={formData.mention}
                            onChange={handleInputChange}
                            required
                            className="und-diploma-form-input"
                        >
                            <optgroup label="Doctorat / HDR">
                                <option value="Très Honorable avec Félicitations du Jury">Très Honorable avec Félicitations du Jury</option>
                                <option value="Très Honorable">Très Honorable</option>
                                <option value="Honorable">Honorable</option>
                            </optgroup>
                            <optgroup label="Master / Licence">
                                <option value="Très Bien">Très Bien</option>
                                <option value="Bien">Bien</option>
                                <option value="Assez Bien">Assez Bien</option>
                                <option value="Passable">Passable</option>
                            </optgroup>
                        </select>
                        <input
                            type="text"
                            name="anneeAcademique"
                            value={formData.anneeAcademique}
                            onChange={handleInputChange}
                            placeholder="Année académique (Ex: 2024-2025)"
                            required
                            className="und-diploma-form-input"
                        />
                    </div>

                    {/* Informations du document */}
                    <div className="und-diploma-form-section-title">Informations du document</div>
                    <div className="und-diploma-form-row">
                        <input
                            type="text"
                            name="titreSuffix"
                            value={formData.titreSuffix}
                            onChange={handleInputChange}
                            placeholder="Titre du diplôme (ex: DE DOCTORAT, D'HABILITATION)"
                            required
                            className="und-diploma-form-input"
                        />
                        <input
                            type="text"
                            name="englishTitle"
                            value={formData.englishTitle}
                            onChange={handleInputChange}
                            placeholder="English Title (ex: Bachelor, PhD, Master)"
                            required
                            className="und-diploma-form-input"
                        />
                    </div>

                    <div className="und-diploma-form-row">
                        <input
                            type="text"
                            name="numeroDoc"
                            value={formData.numeroDoc}
                            onChange={handleInputChange}
                            placeholder="Numéro du document (optionnel)"
                            className="und-diploma-form-input"
                        />
                    </div>

                    <div className="und-diploma-form-row">
                        <input
                            type="date"
                            name="dateEmission"
                            value={formData.dateEmission}
                            onChange={handleInputChange}
                            required
                            className="und-diploma-form-input"
                        />
                        <select
                            name="language"
                            value={formData.language}
                            onChange={handleInputChange}
                            required
                            className="und-diploma-form-input"
                        >
                            <option value="Français + English">Français + English (2 pages)</option>
                            <option value="Français">Français (1 page)</option>
                            <option value="English">English (1 page)</option>
                        </select>
                    </div>

                    <button type="submit" className="und-diploma-submit-btn">
                        {isEditing ? 'Modifier le Diplôme UND - UNILIS' : 'Créer le Diplôme UND - UNILIS'}
                    </button>
                    {isEditing && (
                        <button 
                            type="button" 
                            onClick={resetForm}
                            className="und-diploma-submit-btn"
                            style={{ 
                                marginTop: '10px', 
                                background: 'linear-gradient(135deg, #6c757d, #5a6268)' 
                            }}
                        >
                            Annuler
                        </button>
                    )}
                </form>

                {/* Document List */}
                <div className="und-diploma-document-list">
                    {documents.map((document) => (
                        <div key={document.id} className="und-diploma-document-item">
                            <button
                                className="und-diploma-document-toggle"
                                onClick={() => showDocumentModal(document)}
                            >
                                <Eye size={16} style={{ marginRight: '8px' }} />
                                {document.noms} - UNILIS UND Diploma
                            </button>
                            <button
                                className="und-diploma-document-toggle"
                                onClick={() => handleEditDocument(document)}
                                style={{ 
                                    marginRight: '8px', 
                                    background: '#ffc107',
                                    color: '#000'
                                }}
                            >
                                <Edit size={16} style={{ marginRight: '8px' }} />
                                Modifier
                            </button>
                            <Popconfirm
                                title="Supprimer le document"
                                description="Êtes-vous sûr de vouloir supprimer ce document ?"
                                onConfirm={() => handleDeleteDocument(document.id)}
                                okText="Oui"
                                cancelText="Non"
                                okButtonProps={{ danger: true }}
                            >
                                <button className="und-diploma-delete-btn">
                                    <Delete size={15} />
                                </button>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            <ConfigProvider>
                <Modal
                    title={<span style={{ color: 'white' }}>Aperçu - Diplôme UND UNILIS</span>}
                    open={modalVisible}
                    onCancel={handleModalClose}
                    width="95vw"
                    style={{ top: 10 }}
                    centered={false}
                    maskClosable={true}
                    keyboard={true}
                    footer={[
                        <Button
                            key="download"
                            type="primary"
                            icon={<Download size={16} />}
                            onClick={downloadPDF}
                            style={{
                                backgroundColor: '#28a745',
                                borderColor: '#28a745',
                                fontWeight: 'bold'
                            }}
                        >
                            Télécharger PDF
                        </Button>,
                        <Button
                            key="close"
                            onClick={handleModalClose}
                            style={{ fontWeight: 'bold' }}
                        >
                            Fermer
                        </Button>
                    ]}
                >
                    {selectedDocument && (
                        <div className="und-diploma-modal-content">
                            <UNILISUNDDiplomaDocument document={selectedDocument} qrCodeData={qrCodeData} language={selectedDocument.language || 'Français + English'} />
                        </div>
                    )}
                </Modal>
            </ConfigProvider>
        </div>
    );
};

// Document Component
const UNILISUNDDiplomaDocument = ({ document: docData, qrCodeData, language = 'Français + English' }) => {
    const showBoth = language === 'Français + English';
    const showFrench = language === 'Français' || showBoth;
    const showEnglish = language === 'English' || showBoth;

    return (
        <div id="und-diploma-document" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'transparent',
            padding: '20px',
            width: 'fit-content',
            minWidth: '30cm',
            margin: '0 auto',
            overflowX: 'hidden'
        }}>
            {showFrench && (
                <UNILISUNDDiplomaPage document={docData} qrCodeData={qrCodeData} language="French" />
            )}
            {showEnglish && (
                <UNILISUNDDiplomaPage document={docData} qrCodeData={qrCodeData} language="English" />
            )}
        </div>
    );
};

// Single Page Component
const UNILISUNDDiplomaPage = ({ document: docData, qrCodeData, language = 'French' }) => {
    const formatDateWithMonthName = (dateStr, lang = 'fr') => {
        const date = new Date(dateStr);
        const monthsFr = [
            "janvier", "février", "mars", "avril", "mai", "juin",
            "juillet", "août", "septembre", "octobre", "novembre", "décembre"
        ];
        const monthsEn = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        const months = lang === 'en' ? monthsEn : monthsFr;
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const isEnglish = language === 'English';
    const pageId = `und-diploma-${isEnglish ? 'english' : 'french'}`;

    // Translation mappings
    const translations = {
        'DIPLOME': isEnglish ? 'CERTIFICATE' : 'DIPLOME',
        'REPUBLIQUE DEMOCRATIQUE DU CONGO': isEnglish ? 'DEMOCRATIC REPUBLIC OF CONGO' : 'REPUBLIQUE DEMOCRATIQUE DU CONGO',
        'UNIVERSITE DE LISALA': isEnglish ? 'UNIVERSITY OF LISALA' : 'UNIVERSITE DE LISALA',
        'Vu la Loi-cadre n°14/004 du 11 février 2014 de l\'Enseignement National;': isEnglish 
            ? 'In view of Framework Law No. 14/004 of February 11, 2014 on National Education;'
            : 'Vu la Loi-cadre n°14/004 du 11 février 2014 de l\'Enseignement National;',
        'Vu le décret n° 22/39 du 8 décembre 2022 portant organisation et fonctionnement du système LMD en République Démocratique du Congo;': isEnglish
            ? 'In view of Decree No. 22/39 of December 8, 2022 on the organization and functioning of the LMD system in the Democratic Republic of Congo;'
            : 'Vu le décret n° 22/39 du 8 décembre 2022 portant organisation et fonctionnement du système LMD en République Démocratique du Congo;',
        'Vu le décret n° 24/23. du 15 Mars 2024 relatif aux diplômes et grades académiques;': isEnglish
            ? 'In view of Decree No. 24/23 of March 15, 2024 relating to diplomas and academic degrees;'
            : 'Vu le décret n° 24/23. du 15 Mars 2024 relatif aux diplômes et grades académiques;',
        'Vu l\'arrêté ministériel 401 du 28 août 2023 portant modalités d\'évaluation, de progression et d\'orientation dans les cycles d\'études;': isEnglish
            ? 'In view of Ministerial Order 401 of August 28, 2023 on assessment, progression and orientation modalities in study cycles;'
            : 'Vu l\'arrêté ministériel 401 du 28 août 2023 portant modalités d\'évaluation, de progression et d\'orientation dans les cycles d\'études;',
        'Vu l\'arrêté ministériel n° 133/MINESU/CAB.MIN/NM/RS/2006 du 25 avril 2006 portant création et prise en charge par le trésor public d\'un Centre Universitaire qualifié « Centre Universitaire de Lisala » ;': isEnglish
            ? 'In view of Ministerial Decree No. 133/MINESU/CAB.MIN/NM/RS/2006 of April 25, 2006 establishing and providing for a qualified University Center "University Center of Lisala" by the public treasury;'
            : 'Vu l\'arrêté ministériel n° 133/MINESU/CAB.MIN/NM/RS/2006 du 25 avril 2006 portant création et prise en charge par le trésor public d\'un Centre Universitaire qualifié « Centre Universitaire de Lisala » ;',
        'Vu l\'Arrêté Ministériel 004 MINESU/CABMIN/SASM/MMK/2025 du 06 février 2025 portant organisation et fonctionnement de l\'Enseignement ouvert et à distance au sein des Etablissements de l\'Enseignement Supérieur et Universitaire en République Démocratique du Congo;': isEnglish
            ? 'In view of Ministerial Order 004 MINESU/CABMIN/SASM/MMK/2025 of February 6, 2025 on the organization and functioning of open and distance learning within Higher Education and University Institutions in the Democratic Republic of Congo;'
            : 'Vu l\'Arrêté Ministériel 004 MINESU/CABMIN/SASM/MMK/2025 du 06 février 2025 portant organisation et fonctionnement de l\'Enseignement ouvert et à distance au sein des Etablissements de l\'Enseignement Supérieur et Universitaire en République Démocratique du Congo;',
        'Vu les accords de partenariats de cotutelle internationale de thèses et Co diplomation signés entre le Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) de l\'Université de Lisala en RDC, Groupe de Recherche Appliquée pour le Développement par l\'Innovation de l\'AFrique - GRADIAF International en France ; Université IRGIB AFRICA au Benin et UNCR university;': isEnglish
            ? 'In view of the international co-supervision and co-diplomation partnership agreements signed between the Interuniversity Center for Multidisciplinary Research (CIREP) of the University of Lisala in DRC, Applied Research Group for Development through Innovation in Africa - GRADIAF International in France; IRGIB AFRICA University in Benin and UNCR university;'
            : 'Vu les accords de partenariats de cotutelle internationale de thèses et Co diplomation signés entre le Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) de l\'Université de Lisala en RDC, Groupe de Recherche Appliquée pour le Développement par l\'Innovation de l\'AFrique - GRADIAF International en France ; Université IRGIB AFRICA au Benin et UNCR university;',
        'Vu les procès-verbaux du Jury attestant que l\'intéressé a satisfait au contrôle des compétences prévu par les textes légaux et réglementaires ;': isEnglish
            ? 'In view of the Jury minutes attesting that the interested party has satisfied the competency assessment provided for by legal and regulatory texts;'
            : 'Vu les procès-verbaux du Jury attestant que l\'intéressé a satisfait au contrôle des compétences prévu par les textes légaux et réglementaires ;',
        'Le diplôme de': isEnglish ? 'The degree of' : 'Le diplôme de',
        'en': isEnglish ? 'in' : 'en',
        'Spécialité:': isEnglish ? 'Specialty:' : 'Spécialité:',
        'est délivré à': isEnglish ? 'is awarded to' : 'est délivré à',
        'né(e) le': isEnglish ? 'born on' : 'né(e) le',
        'à': isEnglish ? 'in' : 'à',
        'Province du': isEnglish ? 'Province of' : 'Province du',
        'au titre de l\'année académique': isEnglish ? 'for the academic year' : 'au titre de l\'année académique',
        'avec la mention': isEnglish ? 'with the distinction' : 'avec la mention',
        'et lui confère le grade de': isEnglish ? 'and confers upon him/her the degree of' : 'et lui confère le grade de',
        'pour en jouir avec les droits et prérogatives qui y sont attachés.': isEnglish
            ? 'to enjoy with the rights and prerogatives attached thereto.'
            : 'pour en jouir avec les droits et prérogatives qui y sont attachés.',
        'Fait à Lisala, le': isEnglish ? 'Done in Lisala, on' : 'Fait à Lisala, le',
        'Le Recteur': isEnglish ? 'The Rector' : 'Le Recteur',
        'Directeur de l\'École Doctorale/UNCR University': isEnglish ? 'Director of the Doctoral School/UNCR University' : 'Directeur de l\'École Doctorale/UNCR University',
        'Directeur Général du CIREP': isEnglish ? 'Director General of CIREP' : 'Directeur Général du CIREP'
    };

    const getMentionTranslation = (mention) => {
        if (isEnglish) {
            const mentionMap = {
                'Très Honorable avec Félicitations du Jury': 'Very Honorable with Jury Congratulations',
                'Très Honorable': 'Very Honorable',
                'Honorable': 'Honorable',
                'Très Bien': 'Very Good',
                'Bien': 'Good',
                'Assez Bien': 'Fairly Good',
                'Passable': 'Pass'
            };
            return mentionMap[mention] || mention;
        }
        return mention;
    };

    const getGradeTranslation = (gradeType) => {
        if (isEnglish) {
            if (gradeType === 'Autre') return docData.gradeTypeCustom || gradeType;
            const gradeMap = {
                'Doctorat': 'Doctorate (PhD)',
                'Master': 'Master',
                'HDR': 'HDR',
                'Licence': 'Bachelor'
            };
            return gradeMap[gradeType] || gradeType;
        }
        if (gradeType === 'Autre') return docData.gradeTypeCustom || gradeType;
        if (gradeType === 'Doctorat') return 'Doctorat (PhD)';
        return gradeType;
    };

    return (
        <div className="und-diploma-page" id={pageId} style={{
            width: '29.7cm',
            height: '21cm',
            padding: '2cm',
            margin: '0 auto 30px auto',
            background: 'white',
            color: 'black',
            position: 'relative',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            lineHeight: 1.4,
            pageBreakAfter: 'always',
            pageBreakInside: 'avoid',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            fontFamily: "'Times New Roman', serif"
        }}>

            {/* Header */}
            <div className="und-diploma-header">
                <div className="und-diploma-header-row">
                    <div className="und-diploma-left-logo">
                        <img src={edaplogo} alt="Logo Left" className="und-diploma-logo" />
                    </div>
                    <div className="und-diploma-header-center">
                        <h1 className="und-diploma-country">{translations['REPUBLIQUE DEMOCRATIQUE DU CONGO']}</h1>
                        <h2 className="und-diploma-university">{translations['UNIVERSITE DE LISALA']}</h2>
                        <p className="und-diploma-address" style={{ marginTop: '8px', fontSize: '12px' }}>Avenue Aérodrome n° 20 , Quartier 3C, Commune de Bolikango, Ville de Lisala, Province de la Mongala</p>
                        <p className="und-diploma-address" style={{ fontSize: '12px' }}>P.O. Box 9289 Kinshasa I</p>
                        <p className="und-diploma-contact" style={{ fontSize: '12px' }}>Web: www.universitedelisala.ac.cd ; Email: info@universitedelisala.ad.cd</p>
                    </div>
                    <div className="und-diploma-right-logo">
                        <img src={crlogo} alt="Logo Right" className="und-diploma-logo" />
                    </div>
                </div>
            </div>

            {/* Title Section */}
            <div className="und-diploma-title-section">
                <div className="und-diploma-title-row">
                    <div className="und-diploma-title-spacer"></div>
                    <h1 className="und-diploma-title">
                        {isEnglish 
                            ? `${(docData.englishTitle || 'PhD').toUpperCase()} ${translations['DIPLOME']}`
                            : `${translations['DIPLOME']} ${docData.titreSuffix ? docData.titreSuffix.toUpperCase() : 'DE DOCTORAT'}`
                        }
                    </h1>
                    <div className="und-diploma-number-box">
                        <span className="und-diploma-number-label">N°</span>
                        <span className="und-diploma-number-value">{docData.numeroDoc || ''}</span>
                    </div>
                </div>
            </div>

            {/* Document Body */}
            <div className="und-diploma-document-body">
                <div className="und-diploma-legal-references">
                    <p className="und-diploma-vu-clause">{translations['Vu la Loi-cadre n°14/004 du 11 février 2014 de l\'Enseignement National;']}</p>
                    <p className="und-diploma-vu-clause">{translations['Vu le décret n° 22/39 du 8 décembre 2022 portant organisation et fonctionnement du système LMD en République Démocratique du Congo;']}</p>
                    <p className="und-diploma-vu-clause">{translations['Vu le décret n° 24/23. du 15 Mars 2024 relatif aux diplômes et grades académiques;']}</p>
                    <p className="und-diploma-vu-clause">{translations['Vu l\'arrêté ministériel 401 du 28 août 2023 portant modalités d\'évaluation, de progression et d\'orientation dans les cycles d\'études;']}</p>
                    <p className="und-diploma-vu-clause">{translations['Vu l\'arrêté ministériel n° 133/MINESU/CAB.MIN/NM/RS/2006 du 25 avril 2006 portant création et prise en charge par le trésor public d\'un Centre Universitaire qualifié « Centre Universitaire de Lisala » ;']}</p>
                    <p className="und-diploma-vu-clause">{translations['Vu l\'Arrêté Ministériel 004 MINESU/CABMIN/SASM/MMK/2025 du 06 février 2025 portant organisation et fonctionnement de l\'Enseignement ouvert et à distance au sein des Etablissements de l\'Enseignement Supérieur et Universitaire en République Démocratique du Congo;']}</p>
                    <p className="und-diploma-vu-clause">{translations['Vu les accords de partenariats de cotutelle internationale de thèses et Co diplomation signés entre le Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP) de l\'Université de Lisala en RDC, Groupe de Recherche Appliquée pour le Développement par l\'Innovation de l\'AFrique - GRADIAF International en France ; Université IRGIB AFRICA au Benin et UNCR university;']}</p>
                    <p className="und-diploma-vu-clause">{translations['Vu les procès-verbaux du Jury attestant que l\'intéressé a satisfait au contrôle des compétences prévu par les textes légaux et réglementaires ;']}</p>
                </div>

                <div className="und-diploma-declaration">
                    <p className="und-diploma-main-declaration">
                        {translations['Le diplôme de']} <strong>{getGradeTranslation(docData.gradeType)}</strong> {translations['en']} <strong>{docData.department}</strong> {translations['Spécialité:']} <strong>{docData.specialite}</strong> {translations['est délivré à']} <strong>{docData.noms}</strong> {translations['né(e) le']} <strong>{formatDateWithMonthName(docData.dateNaissance, isEnglish ? 'en' : 'fr')}</strong> {translations['à']} <strong>{docData.lieuNaissance}</strong>{docData.province ? ` ${translations['Province du']} ${docData.province}` : ''}/<strong>{docData.pays}</strong> {translations['au titre de l\'année académique']} <strong>{docData.anneeAcademique}</strong> {translations['avec la mention']} <strong>{getMentionTranslation(docData.mention)}</strong> {translations['et lui confère le grade de']} {getGradeTranslation(docData.gradeType)}, {translations['pour en jouir avec les droits et prérogatives qui y sont attachés.']}
                    </p>
                </div>

                <div className="und-diploma-emission-date">
                    <p>{translations['Fait à Lisala, le']} <strong>{formatDateWithMonthName(docData.dateEmission, isEnglish ? 'en' : 'fr')}</strong></p>
                </div>
            </div>

            {/* Signatures */}
            <div className="und-diploma-signatures">
                <div className="und-diploma-signature-row">
                    <div className="und-diploma-signature-item">
                        <img src={rectorSig} alt="Rector Signature" style={{ width: '120px', height: '60px', objectFit: 'contain', marginBottom: '8px' }} />
                        <hr className="und-diploma-signature-line-separator" />
                        <p className="und-diploma-signature-name">Professeur Abbé Donat TEBAKABE Alomo</p>
                        <p className="und-diploma-signature-title">{translations['Le Recteur']}</p>
                    </div>
                    <div className="und-diploma-signature-item">
                        <img src={perezSig} alt="Perez Signature" style={{ width: '120px', height: '60px', objectFit: 'contain', marginBottom: '8px' }} />
                        <hr className="und-diploma-signature-line-separator" />
                        <p className="und-diploma-signature-name">Pr Jean Luc Perez</p>
                        <p className="und-diploma-signature-title">{translations['Directeur de l\'École Doctorale/UNCR University']}</p>
                    </div>
                    <div className="und-diploma-signature-item">
                        <img src={bapSig} alt="Perez Signature" style={{ width: '120px', height: '60px', objectFit: 'contain', marginBottom: '8px' }} />
                        <hr className="und-diploma-signature-line-separator" />
                        <p className="und-diploma-signature-name">Pr NIZEYIMANA Jean Baptiste</p>
                        <p className="und-diploma-signature-title">{translations['Directeur Général du CIREP']}</p>
                    </div>
                    {qrCodeData && (
                        <div className="und-diploma-qr-section">
                            <img
                                src={qrCodeData}
                                alt="QR Code - Document Verification"
                                className="und-diploma-qr-code"
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    border: '1px solid #000'
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UNILISUNDDiplomaTemplate;
